// Native
import { useNavigate, useParams } from "react-router"
import { Link } from "react-router-dom"

// Components
import { Avatar, Button, ButtonGroup, Center, Container, Divider, Flex, Grid, GridItem, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from "@chakra-ui/react"
import { Alert, Clipboard, Icon, Label, IconButton, Breadcrumb, Dev, Menu, Scope, Tag, Show } from "components"

// Hooks
import { useConsultant, useUser } from "hooks"

// Config
import { Mask, Moment } from "config"

// Parts
import General from "./general"
import Orders from "./orders"
import Addresses from "./addresses"
import Banks from "./banks"
import Block from "./block"
import Edit from './edit'
import Extract from "./extract"
import Cycle from "./cycle"
import Withdraws from "./withdraws"
import Identity from "./identity"
import Distributor from "./distributor"
import Password from "./password"
import Files from "pages/common/files"
import Network from "./network"
import { Enums } from "config"
import { Functions } from "config"
import { API } from "config"
import Migrate from "./migrate"
import Expire from "./expire"

export default function Consultant(){

    const toast = useToast()
    const navigate = useNavigate()
    const params = useParams()
    const consultant = useConsultant(params.id)
    const user = useUser()
    const roles = user.data?.roles

    const edit = useDisclosure()
    const block = useDisclosure()
    const identity = useDisclosure()
    const distributor = useDisclosure()
    const password = useDisclosure()
    const migrate = useDisclosure()
    const expire = useDisclosure()

    const activeMonthStatus = Enums.ActiveMonthStatus.data(consultant.data?.active_month_status)

    const handleRemoteAccess = async () => {
        if(!window.confirm('Tem certeza que deseja iniciar o acesso ao escritório do cliente?')) return
        toast({ title: 'Iniciando acesso remoto...' })

        try{
            const callback = await API.get(`/consultants/${consultant.data?.id}/remote`)
            toast({ title: 'Acesso remoto iniciado.', description: 'Sua sessão é monitorada' })
            window.open(callback.data?.url)
        }catch(err){
            toast({ title: 'Não foi possível iniciar o acesso remoto', description: err?.response?.data?.message, status: 'warning' })
        }
    }

    const handleLogoutConsultant = async () => {
        if(!window.confirm('Tem certeza que deseja deslogar este consultor de todas as sessões?')) return

        try {
            await API.post(`/sessions/consultant/${consultant.data?.id}/logout`)
            toast({ title: 'Sucesso', description: 'O consultor foi deslogado com sucesso.', status: 'success' })
        } catch(err) {
            toast({
                title: 'Erro ao deslogar',
                description: err?.response?.data?.message || 'Ocorreu um erro inesperado',
                status: 'warning'
            })
        }
    }

    return(
    <>
    <Container maxW="90%" mt={10}>

        <Grid templateColumns="50% 50%" alignItems="center">

            {/* Breadcrumbs */}
            <GridItem><Breadcrumb prev={{ path: '/consultants', label: 'Consultores' }} current="Perfil de consultor" /></GridItem>

            {/* Actions */}
            <GridItem display="flex" justifyContent="flex-end">
                <ButtonGroup isDisabled={consultant.isFetching} size="xs">
                    <IconButton isLoading={consultant.isFetching} onClick={consultant.refetch} icon="refresh" />

                    {/* Permissão: manage_consultants */}
                    <Scope role="consultants_update">
                        <IconButton onClick={block.onOpen} colorScheme="orange" icon="block" />
                        <Button onClick={edit.onOpen} leftIcon={<Icon name="edit" />}>Editar</Button>
                        <Scope role="tools_network_migrate"><IconButton label="Migração de rede" onClick={migrate.onOpen} colorScheme="red" icon="transfer" /></Scope>
                        <Show visible={!consultant.data?.is_ghost}><IconButton label="Expirar cadastro" onClick={expire.onOpen} colorScheme="red" icon="trash" /></Show>
                        <Menu icon="more" options={[
                            { children: 'Criar núcleo de distribuição', isDisabled: !!consultant?.data?.customer?.distributor || !Functions.hasRole(user, 'distributors_create'), icon: 'distributor', onClick: distributor.onOpen },
                            { children: 'Verificar identidade', icon: 'identity', isDisabled: !!consultant.data?.customer?.identity_verified_at || !Functions.hasRole(user, 'consultants_identity'), onClick: identity.onOpen },
                            { children: 'Redefinir senha', icon: 'password', onClick: password.onOpen },
                            { children: 'Acessar escritório', icon: 'security', onClick: handleRemoteAccess, isDisabled: !consultant.data?.customer?.options?.support_remote || !Functions.hasRole(user, 'tools_remote_access') },

                            // Usamos um valor nulo ou condicional aqui
                            user.data?.roles?.includes('logout_consultant') ? {
                                children: 'Deslogar consultor',
                                icon: 'logout',
                                onClick: handleLogoutConsultant
                            } : null,
                        ].filter(Boolean)} />

                    </Scope>

                    <Dev data={consultant.data} />
                </ButtonGroup>
            </GridItem>

        </Grid>

        <Divider my={4} />

        <Grid templateColumns="20% 80%">

            <GridItem>
                <Stack spacing={4}>

                    <Flex direction="column" alignItems="center" justifyContent="center">
                        <Avatar size="xl" name={consultant.data?.customer?.name} src={consultant.data?.customer?.avatar_url} mb={2} />
                        <Text textAlign="center" textTransform="capitalize" fontSize="lg" children={consultant.data?.customer?.name} />
                        <Text fontSize="small" color="GrayText" children={`Membro ${Moment(consultant.data?.created_at).fromNow()}`} />
                    </Flex>

                    <Center><Tag mt={2} colorScheme={activeMonthStatus.colorScheme} children={`${activeMonthStatus.emoji} Consultor ${activeMonthStatus.label}`} /></Center>

                    <Divider />

                    <Label label="Localização:" children={consultant.data?.customer?.city && `🗺️ ${consultant?.data?.customer?.city?.name}, ${consultant?.data?.customer?.city?.state_code}`} />

                    <Divider />

                    <Label label="Endereço de e-mail:"><Link to={`mailto:${consultant.data?.customer?.email}`}>{consultant.data?.customer?.email}</Link></Label>
                    <Label label="Telefone:"><a target="_blank" href={`https://wa.me/${consultant?.data?.customer?.mobile}`}>{consultant?.data?.customer?.mobile}</a></Label>
                    <Show visible={!!consultant?.data?.customer?.doc_cpf}><Label label="CPF:" children={<Clipboard children={Mask.cpf(consultant?.data?.customer?.doc_cpf)} />} /></Show>
                    <Show visible={!!consultant?.data?.customer?.doc_international}><Label label="Documento internacional:" children={<Clipboard children={consultant?.data?.customer?.doc_international} />} /></Show>

                    <Divider />

                    <Show visible={!!consultant.data?.upline?.id}><Label label="Upline:" textTransform="capitalize"><Link to={`/consultants/${consultant.data?.upline?.id}`}>{consultant?.data?.upline?.customer?.name}</Link></Label></Show>
                    <Show visible={!consultant.data?.upline?.id}><Label label="Upline:" textTransform="capitalize">Usuário Expirado</Label></Show>

                    <Divider />

                    <Label label="ID do Consultor:" children={<Clipboard children={consultant.data?.id} />} fontSize="xs" />
                    <Label label="ID do Consultor (Legado):" children={<Clipboard children={consultant.data?.cod} />} fontSize="xs" />
                    <Label label="ID do Cliente:"  children={<Clipboard children={consultant.data?.customer?.id} />} fontSize="xs" />

                </Stack>
            </GridItem>

            <GridItem>

                {/* Mensagens de alert */}
                <Stack mb={4}>
                    { !!consultant?.data?.customer?.blocked_at && <Alert title="Cliente bloqueado." status="warning" children={`Este cliente foi bloqueado ${Moment(consultant?.data?.customer?.blocked_at).calendar()} pelo motivo: ${consultant?.data?.customer?.blocked_reason}.`} /> }
                    { !!consultant?.data?.customer?.distributor && <Alert cursor="pointer" onClick={() => navigate(`/distributors/${consultant.data?.customer?.distributor?.id}`)} title="Proprietário de núcleo" status="info" children={`Este cliente também é proprietário do núcleo: ${consultant.data?.customer?.distributor?.name}`} /> }
                    { !consultant?.data?.customer?.identity_verified_at && <Alert cursor="pointer" onClick={identity.onOpen} title="Identidade não verificada" status="error" children={`Este cliente ainda não teve a sua identidade verificada. Clique aqui para verificar.`} /> }
                </Stack>

                {/* Tabs */}
                <Tabs isLazy size="sm" variant="enclosed" colorScheme="orange">

                    <TabList>
                        <Tab isDisabled={consultant.isFetching}>Visão Geral</Tab>
                        <Tab isDisabled={consultant.isFetching}>Rede</Tab>
                        { Functions.hasRole(user, 'orders_view') && <Tab isDisabled={consultant.isFetching}>Pedidos</Tab> }
                        <Tab isDisabled={consultant.isFetching}>Endereços</Tab>
                        <Tab isDisabled={consultant.isFetching}>Contas Bancárias</Tab>
                        <Tab isDisabled={consultant.isFetching}>Saques</Tab>
                        <Tab isDisabled={consultant.isFetching}>Extrato</Tab>
                        <Tab isDisabled={consultant.isFetching}>Ciclo</Tab>
                        <Tab isDisabled={consultant.isFetching}>Arquivos</Tab>
                    </TabList>

                    <TabPanels>

                        {/* Visão Geral */}
                        <TabPanel><General data={consultant.data} /></TabPanel>

                        {/* Rede */}
                        <TabPanel><Network /></TabPanel>

                        {/* Pedidos */}
                        { Functions.hasRole(user, 'orders_view') && <TabPanel><Orders data={consultant.data} /></TabPanel> }

                        {/* Endereços */}
                        <TabPanel><Addresses data={consultant.data} refetch={consultant.refetch} /></TabPanel>

                        {/* Contas Bancárias */}
                        <TabPanel><Banks data={consultant.data} refetch={consultant.refetch} /></TabPanel>

                        {/* Saques */}
                        <TabPanel><Withdraws data={consultant.data} refetch={consultant.refetch} /></TabPanel>

                        {/* Extrato */}
                        <TabPanel><Extract data={consultant} /></TabPanel>

                        {/* Ciclos */}
                        <TabPanel><Cycle data={consultant} /></TabPanel>

                        {/* Arquivos */}
                        <TabPanel><Files data={consultant.data} model="consultants" title="Arquivos do consultor" options={{ consultant_id: consultant?.data?.id }} /></TabPanel>

                    </TabPanels>
                </Tabs>
            </GridItem>
        </Grid>
    </Container>

    {/* Screens */}
    <Edit {...edit} data={consultant.data} onSuccess={consultant.refetch} />
    <Block {...block} data={consultant.data} onSuccess={consultant.refetch}  />
    <Identity {...identity} data={consultant.data} onSuccess={consultant.refetch} />
    <Distributor {...distributor} data={consultant.data} onSuccess={consultant.refetch} />
    <Password {...password} data={consultant.data} onSuccess={consultant.refetch} />
    <Migrate {...migrate} data={consultant.data} onSuccess={consultant.refetch} />
    <Expire {...expire} />

    </>
    )
}